/* eslint-disable indent */
/* eslint-disable no-use-before-define */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  TextField,
  Checkbox,
  Spinner,
  Icon,
} from '@folio/stripes-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { useStripes } from '@folio/stripes/core';
import { FaBarcode } from 'react-icons/fa';
import { IoSettings } from 'react-icons/io5';
import useBarcodeValidation from '../../../../../../../hooks/useBarcodeValidation';

/**
 * Reusable Sequential Barcode Generation Section
 * Used across multiple modals for consistent barcode generation UI
 */
const SequentialBarcodeSection = ({
  // Required props
  itemCount = 1,
  onBarcodeSettingsChange,

  // Optional props with defaults
  enabled = true,
  checkboxLabel,
  sectionTitle,
  placeholder,
  showAdvancedButton = true,
  onAdvancedClick,
  validationMessages = {},

  // Auto-generation props (for AddVolumeModal compatibility)
  enableAutoGenerator = false,
  autoGenerate = false,
  existingBarcodes = [],

  // Custom validation function (optional)
  customValidationFunction,
}) => {
  const intl = useIntl();
  const { okapi } = useStripes();
  const { validateBarcodeSequence, generateBarcodePreview } =
    useBarcodeValidation();

  // Internal state
  const [useSequentialBarcode, setUseSequentialBarcode] = useState(enabled);
  const [startingBarcode, setStartingBarcode] = useState('');
  const [barcodeValidation, setBarcodeValidation] = useState({
    isValid: true,
    message: '',
    isChecking: false,
    conflicts: [],
    conflictCount: 0,
  });

  // Ref for debounce timeout management
  const validationTimeoutRef = useRef(null);

  // Default validation messages
  const defaultValidationMessages = {
    empty: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcode.validation.empty',
    }),
    duplicate: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcode.validation.duplicate',
    }),
    invalidCharacters: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcode.validation.invalidCharacters',
    }),
    tooShort: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcode.validation.tooShort',
    }),
    valid: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcode.validation.valid',
    }),
    error: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcode.validation.error',
    }),
    ...validationMessages,
  };

  // Default labels
  const defaultCheckboxLabel =
    checkboxLabel ||
    intl.formatMessage({
      id: 'ui-plugin-inventory-templates.sequentialBarcode.useSequential.label',
      defaultMessage: 'Generate sequential barcodes',
    });

  const defaultSectionTitle =
    sectionTitle ||
    intl.formatMessage(
      {
        id: 'ui-plugin-inventory-templates.sequentialBarcode.section.title',
        defaultMessage: 'Barcode Generation ({count} items)',
      },
      { count: Math.max(1, parseInt(itemCount, 10) || 1) },
    );

  const defaultPlaceholder =
    placeholder ||
    intl.formatMessage({
      id: 'ui-plugin-inventory-templates.sequentialBarcode.startingBarcode.placeholder',
      defaultMessage: 'e.g., BOOK2024001',
    });

  // Reset when enabled state changes
  useEffect(() => {
    if (!enabled) {
      setUseSequentialBarcode(false);
      setStartingBarcode('');
      // Clear any pending validation
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
      setBarcodeValidation({
        isValid: true,
        message: '',
        isChecking: false,
      });
    }
  }, [enabled]);

  // Re-validate when item count changes (if barcode is already entered)
  useEffect(() => {
    if (
      startingBarcode &&
      startingBarcode.trim() !== '' &&
      useSequentialBarcode
    ) {
      performValidation(startingBarcode, itemCount);
    }
  }, [itemCount]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  // Enhanced validation function using the new barcode validation system
  const validateBarcodeRange = async (startBarcode, count) => {
    if (!startBarcode || !count) {
      return {
        isValid: true,
        message: '',
        conflicts: [],
        conflictCount: 0,
      };
    }

    try {
      const validation = await validateBarcodeSequence(startBarcode, count, {
        existingBarcodes,
        customValidation: customValidationFunction,
        okapi, // إضافة okapi للتحقق من قاعدة البيانات
      });

      return {
        isValid: validation.isValid,
        message: validation.message,
        conflicts: validation.conflicts || [],
        conflictCount: validation.conflictCount || 0,
        type: validation.type,
      };
    } catch (error) {
      console.error('Barcode validation error:', error);
      return {
        isValid: false,
        message: defaultValidationMessages.error,
        conflicts: [],
        conflictCount: 0,
        type: 'ERROR',
      };
    }
  };

  // Perform validation with debouncing
  const performValidation = async (barcode, count) => {
    // Clear any existing timeout
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    setBarcodeValidation({
      isChecking: true,
      isValid: false,
      message: '',
      conflicts: [],
      conflictCount: 0,
    });

    // Set new timeout for debounced validation
    validationTimeoutRef.current = setTimeout(async () => {
      const validItemCount = Math.max(1, parseInt(count, 10) || 1);
      const validation = await validateBarcodeRange(barcode, validItemCount);
      setBarcodeValidation({
        isValid: validation.isValid,
        message: validation.message,
        isChecking: false,
        conflicts: validation.conflicts,
        conflictCount: validation.conflictCount,
        type: validation.type,
      });
    }, 500);
  };

  // Handle barcode change with validation
  const handleStartingBarcodeChange = (e) => {
    const value = e.target.value;
    setStartingBarcode(value);

    // Trigger validation for the entire range
    if (value && value.trim() !== '') {
      performValidation(value, itemCount);
    } else {
      // Clear any pending validation
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
      setBarcodeValidation({
        isValid: true,
        message: '',
        isChecking: false,
        conflicts: [],
        conflictCount: 0,
      });
    }
  };

  // Generate sequential barcode
  // const generateSequentialBarcode = (startBarcode, index) => {
  //   if (!startBarcode) return '';

  //   const match = startBarcode.match(/^(.*)(\d+)$/);
  //   if (match) {
  //     const prefix = match[1];
  //     const startNumber = parseInt(match[2], 10);
  //     const newNumber = startNumber + index;
  //     const originalLength = match[2].length;
  //     const paddedNumber = newNumber.toString().padStart(originalLength, '0');
  //     return `${prefix}${paddedNumber}`;
  //   } else {
  //     const paddedIndex = (index + 1).toString().padStart(3, '0');
  //     return `${startBarcode}${paddedIndex}`;
  //   }
  // };

  /**
   * Generates a sequential barcode by incrementing a numeric suffix.
   *
   * @param {string} startBarcode - The initial barcode (e.g., "BOOK001").
   * @param {number} index - The number to increment from the starting barcode.
   * @returns {string} - The new sequential barcode (e.g., "BOOK002").
   */
  const generateSequentialBarcode = (startBarcode, index) => {
    if (!startBarcode) return '';

    // Match the prefix and the numeric suffix at the end of the string
    const match = startBarcode.match(/^(.*?)(\d{1,})$/);
    if (match) {
      const prefix = match[1]; // Everything before the number
      const startNumberStr = match[2]; // The numeric part as a string
      const startNumber = parseInt(startNumberStr, 10); // Convert to number
      const newNumber = startNumber + index; // Increment the number
      const paddedNumber = newNumber
        .toString()
        .padStart(startNumberStr.length, '0'); // Keep original length

      return `${prefix}${paddedNumber}`; // Combine prefix and padded number
    } else {
      // If there's no numeric suffix, just append the index with 3-digit padding
      const paddedIndex = (index + 1).toString().padStart(3, '0');
      return `${startBarcode}${paddedIndex}`;
    }
  };

  // Handle checkbox change
  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setUseSequentialBarcode(checked);

    // Notify parent component of changes
    if (onBarcodeSettingsChange) {
      const barcodes = [];
      if (checked && startingBarcode.trim() && barcodeValidation.isValid) {
        const validItemCount = Math.max(1, parseInt(itemCount, 10) || 1);
        for (let i = 0; i < validItemCount; i++) {
          barcodes.push(generateSequentialBarcode(startingBarcode, i));
        }
      }
      onBarcodeSettingsChange({
        enabled: checked,
        startingBarcode,
        barcodes,
        isValid: !checked || (startingBarcode && barcodeValidation.isValid),
      });
    }
  };

  // Handle starting barcode change notification to parent
  useEffect(() => {
    if (onBarcodeSettingsChange && useSequentialBarcode) {
      const barcodes = [];
      if (startingBarcode.trim() && barcodeValidation.isValid) {
        const validItemCount = Math.max(1, parseInt(itemCount, 10) || 1);
        for (let i = 0; i < validItemCount; i++) {
          barcodes.push(generateSequentialBarcode(startingBarcode, i));
        }
      }
      onBarcodeSettingsChange({
        enabled: useSequentialBarcode,
        startingBarcode,
        barcodes,
        isValid:
          !useSequentialBarcode ||
          (startingBarcode && barcodeValidation.isValid),
      });
    }
  }, [
    startingBarcode,
    barcodeValidation.isValid,
    useSequentialBarcode,
    itemCount,
  ]);

  // Component to render validation error details
  const renderValidationDetails = () => {
    if (
      !barcodeValidation.conflicts ||
      barcodeValidation.conflicts.length === 0
    ) {
      return null;
    }

    const conflicts = barcodeValidation.conflicts;
    const showAllConflicts = conflicts.length <= 5;
    const displayedConflicts = showAllConflicts
      ? conflicts
      : conflicts.slice(0, 5);

    // Determine the primary issue type to show appropriate title and description
    const conflictTypes = conflicts.reduce((acc, conflict) => {
      acc[conflict.type] = (acc[conflict.type] || 0) + 1;
      return acc;
    }, {});

    // Determine title and description based on issue type
    let titleKey;
    let descriptionKey;
    if (conflictTypes.INVALID_FORMAT) {
      titleKey =
        'ui-plugin-inventory-templates.barcode.validation.invalidCharacters';
      descriptionKey =
        'ui-plugin-inventory-templates.barcode.validation.formatIssues.description';
    } else if (conflictTypes.TOO_SHORT) {
      titleKey = 'ui-plugin-inventory-templates.barcode.validation.tooShort';
      descriptionKey =
        'ui-plugin-inventory-templates.barcode.validation.lengthIssues.description';
    } else if (conflictTypes.GENERATION_ERROR) {
      titleKey = 'ui-plugin-inventory-templates.barcode.validation.error';
      descriptionKey =
        'ui-plugin-inventory-templates.barcode.validation.generationIssues.description';
    } else {
      // For actual conflicts (duplicates)
      titleKey = 'ui-plugin-inventory-templates.barcode.conflicts.title';
      descriptionKey =
        'ui-plugin-inventory-templates.barcode.conflicts.description';
    }

    // Determine colors based on issue type
    const isFormatOrLengthIssue =
      conflictTypes.INVALID_FORMAT || conflictTypes.TOO_SHORT;
    const backgroundColor = isFormatOrLengthIssue ? '#ffe6e6' : '#fff3cd';
    const borderColor = isFormatOrLengthIssue ? '#ffaaaa' : '#ffeaa7';
    const textColor = isFormatOrLengthIssue ? '#8b0000' : '#856404';

    return (
      <div
        style={{
          backgroundColor,
          border: `1px solid ${borderColor}`,
          borderRadius: '4px',
          padding: '12px',
          marginTop: '8px',
        }}
      >
        <div
          style={{ fontWeight: '600', color: textColor, marginBottom: '8px' }}
        >
          <FormattedMessage
            id={titleKey}
            defaultMessage="Validation Issues Detected"
          />
        </div>
        <div
          style={{ fontSize: '12px', color: textColor, marginBottom: '8px' }}
        >
          <FormattedMessage
            id={descriptionKey}
            defaultMessage="The following issues were found:"
          />
        </div>
        <div style={{ fontFamily: 'monospace', fontSize: '11px' }}>
          {displayedConflicts.map((conflict, index) => (
            <div key={index} style={{ marginBottom: '4px' }}>
              <span style={{ color: '#dc3545', fontWeight: '600' }}>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.barcode.conflicts.position"
                  defaultMessage="Position {position}"
                  values={{ position: conflict.position }}
                />
                :
              </span>{' '}
              <span style={{ color: '#495057' }}>{conflict.barcode}</span>
              {conflict.type &&
                (conflict.type === 'SESSION_DUPLICATE' ||
                  conflict.type === 'FORM_DUPLICATE' ||
                  conflict.type === 'DB_DUPLICATE') && (
                  <span style={{ color: '#6c757d', marginLeft: '8px' }}>
                    (
                    <FormattedMessage
                      id={`ui-plugin-inventory-templates.barcode.conflicts.${
                        conflict.type === 'SESSION_DUPLICATE'
                          ? 'sessionConflict'
                          : conflict.type === 'FORM_DUPLICATE'
                            ? 'formConflict'
                            : 'dbConflict'
                      }`}
                      defaultMessage={
                        conflict.type === 'SESSION_DUPLICATE'
                          ? 'Session conflict'
                          : conflict.type === 'FORM_DUPLICATE'
                            ? 'Form conflict'
                            : 'Database conflict'
                      }
                    />
                    )
                  </span>
                )}
            </div>
          ))}
          {!showAllConflicts && (
            <div
              style={{
                fontStyle: 'italic',
                color: '#6c757d',
                marginTop: '4px',
              }}
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.barcode.conflicts.showAll"
                defaultMessage="Show all {count} conflicts"
                values={{ count: conflicts.length }}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const validItemCount = Math.max(1, parseInt(itemCount, 10) || 1);
  const MAX_ITEMS = 1000;
  const showMaxItemsWarning = validItemCount > MAX_ITEMS * 0.8; // Show warning at 80% of limit

  return (
    <div
      style={{
        marginBottom: '16px',
        padding: '16px',
        backgroundColor: '#f0f8ff',
        border: '1px solid #b3d9ff',
        borderRadius: '6px',
      }}
    >
      {/* Section Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '12px',
        }}
      >
        <FaBarcode size={20} style={{ color: '#0c5460' }} />
        <div style={{ fontWeight: '600', color: '#0c5460' }}>
          {defaultSectionTitle}
        </div>
      </div>

      {/* Auto-generation info (for AddVolumeModal compatibility) */}
      {enableAutoGenerator && autoGenerate && (
        <div
          style={{ marginBottom: '12px', fontSize: '14px', color: '#28a745' }}
        >
          <FormattedMessage
            id="ui-plugin-inventory-templates.sequentialBarcode.autoGenerationEnabled"
            defaultMessage="✓ Automatic barcode generation is enabled"
          />
        </div>
      )}

      {enableAutoGenerator && !autoGenerate && (
        <div
          style={{ marginBottom: '12px', fontSize: '14px', color: '#6c757d' }}
        >
          <FormattedMessage
            id="ui-plugin-inventory-templates.sequentialBarcode.autoGenerationDisabled"
            defaultMessage="ℹ Automatic generation disabled - barcodes will be empty"
          />
        </div>
      )}

      {/* Maximum items warning */}
      {showMaxItemsWarning && (
        <div
          style={{
            marginBottom: '12px',
            fontSize: '14px',
            color: '#856404',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '4px',
            padding: '8px',
          }}
        >
          <FormattedMessage
            id="ui-plugin-inventory-templates.sequentialBarcode.maxItemsWarning"
            defaultMessage="⚠️ Maximum {max} items can be created at once"
            values={{ max: MAX_ITEMS }}
          />
        </div>
      )}

      {/* Sequential barcode checkbox */}
      <div style={{ marginBottom: '12px' }}>
        <Checkbox
          label={defaultCheckboxLabel}
          checked={useSequentialBarcode}
          onChange={handleCheckboxChange}
        />
      </div>

      {/* Starting barcode input */}
      {useSequentialBarcode && (
        <div style={{ marginBottom: '12px' }}>
          <TextField
            label={intl.formatMessage({
              id: 'ui-plugin-inventory-templates.sequentialBarcode.startingBarcode.label',
              defaultMessage: 'Starting Barcode',
            })}
            value={startingBarcode}
            onChange={handleStartingBarcodeChange}
            placeholder={defaultPlaceholder}
            required
            error={
              !barcodeValidation.isValid && startingBarcode
                ? barcodeValidation.message
                : ''
            }
            endControl={
              barcodeValidation.isChecking ? (
                <Spinner size="small" />
              ) : barcodeValidation.isValid && startingBarcode ? (
                <Icon icon="check-circle" status="success" />
              ) : null
            }
          />
        </div>
      )}

      {/* Validation error details */}
      {useSequentialBarcode &&
        startingBarcode &&
        !barcodeValidation.isValid &&
        renderValidationDetails()}

      {/* Barcode preview */}
      {useSequentialBarcode && startingBarcode && (
        <div
          style={{
            backgroundColor: '#ffffff',
            padding: '10px',
            borderRadius: '4px',
            border: '1px solid #dee2e6',
            marginBottom: '12px',
          }}
        >
          <div
            style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#495057',
              marginBottom: '6px',
            }}
          >
            <FormattedMessage
              id="ui-plugin-inventory-templates.sequentialBarcode.barcodePreview.title"
              defaultMessage="Preview:"
            />
          </div>
          <div
            style={{
              fontFamily: 'monospace',
              fontSize: '12px',
              color: '#6c757d',
            }}
          >
            {Array.from({ length: Math.min(3, validItemCount) }, (_, i) => (
              <div key={i}>
                {i + 1}. {generateSequentialBarcode(startingBarcode, i)}
              </div>
            ))}
            {validItemCount > 3 && (
              <div style={{ fontStyle: 'italic', marginTop: '4px' }}>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.sequentialBarcode.barcodePreview.more"
                  values={{ remaining: validItemCount - 3 }}
                  defaultMessage="... and {remaining} more"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Advanced settings button */}
      {showAdvancedButton && (
        <div style={{ textAlign: 'center' }}>
          <Button buttonStyle="default" onClick={onAdvancedClick} marginBottom0>
            <IoSettings size={16} style={{ marginRight: '4px' }} />
            <FormattedMessage
              id="ui-plugin-inventory-templates.sequentialBarcode.advancedSettings"
              defaultMessage="Advanced Barcode Settings"
            />
          </Button>
        </div>
      )}
    </div>
  );
};

SequentialBarcodeSection.propTypes = {
  // Required props
  itemCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  onBarcodeSettingsChange: PropTypes.func.isRequired,

  // Optional props
  enabled: PropTypes.bool,
  checkboxLabel: PropTypes.string,
  sectionTitle: PropTypes.string,
  placeholder: PropTypes.string,
  showAdvancedButton: PropTypes.bool,
  onAdvancedClick: PropTypes.func,
  validationMessages: PropTypes.object,

  // Auto-generation props
  enableAutoGenerator: PropTypes.bool,
  autoGenerate: PropTypes.bool,
  existingBarcodes: PropTypes.arrayOf(PropTypes.string),

  // Custom validation
  customValidationFunction: PropTypes.func,
};

export default SequentialBarcodeSection;
